import React from 'react'
import PropTypes from 'prop-types'
import Tooltip from '../Tooltip'

// `flex!` beats Bootstrap reboot's `header { display: block }`, which wins over Tailwind utilities.
const HEADER_CLASS = 'tw:flex! tw:items-center tw:gap-2.5 '
  + 'tw:border-b tw:border-solid tw:border-gray-300'

// Default kept so existing callers are unaffected; they can override via `paddingClassName`.
const PADDING_CLASS = 'tw:p-6'

// `!` forces Tailwind over the Bootstrap heading reset; `truncate` holds the heading on one line.
const TITLE_CLASS = 'tw:text-lg! tw:font-medium! tw:m-0! tw:text-gray-900! '
  + 'tw:leading-none! tw:flex-1 tw:min-w-0 tw:truncate'

// `rounded-full!` overrides Bootstrap `$enable-rounded: false` (zeroes radius).
const CLOSE_BUTTON_CLASS = 'tw:inline-flex tw:items-center tw:justify-center '
  + 'tw:size-8 tw:rounded-full! tw:bg-white tw:border-0 tw:cursor-pointer '
  + 'tw:text-gray-900 tw:hover:bg-gray-100 tw:focus-visible:outline-none'

// The `back` variant puts a left chevron before the title.
// The `dismiss` variant puts a close cross at the right (title owns `flex-1`).
const CLOSE_ICON = { back: 'fa-chevron-left', dismiss: 'fa-times' }

const renderHeading = children => {
  if (typeof children === 'string')
    return <h2 className={TITLE_CLASS} title={children}>{children}</h2>
  return children
}

const renderClose = ({ onClose, closeVariant, closeButtonClassName, closeTooltip }) => {
  if (!onClose)
    return null
  const button = (
    <button
      aria-label="Fermer le panneau"
      className={closeButtonClassName}
      type="button"
      onClick={onClose}
    >
      <i aria-hidden="true" className={`fas ${CLOSE_ICON[closeVariant]} tw:text-xl`} />
    </button>
  )
  if (!closeTooltip)
    return button
  return <Tooltip className="tw:inline-flex" content={closeTooltip} placement="bottom">{button}</Tooltip>
}

const DrawerHeader = ({
  children, className, onClose, closeVariant, closeButtonClassName, closeTooltip, paddingClassName
}) => {
  const close = renderClose({ onClose, closeVariant, closeButtonClassName, closeTooltip })
  return (
    <header className={`${HEADER_CLASS} ${paddingClassName} ${className}`}>
      {closeVariant === 'dismiss' ? (
        <>
          {renderHeading(children)}
          {close}
        </>
      ) : (
        <>
          {close}
          {renderHeading(children)}
        </>
      )}
    </header>
  )
}

DrawerHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  closeTooltip: PropTypes.string,
  closeVariant: PropTypes.oneOf(['back', 'dismiss']),
  onClose: PropTypes.func,
  paddingClassName: PropTypes.string
}

DrawerHeader.defaultProps = {
  className: '',
  closeButtonClassName: CLOSE_BUTTON_CLASS,
  closeTooltip: null,
  closeVariant: 'back',
  onClose: null,
  paddingClassName: PADDING_CLASS
}

export default DrawerHeader
